import React, { useState, useCallback, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import { EmptyState } from "../components/common/EmptyState";
import { ClockIcon } from "../components/icons";
import SyncIcon from "../components/icons/SyncIcon";
import BulkActionsToolbar from "../components/grants/recommendations/BulkActionsToolbar";
import BulkRejectModal from "../components/grants/recommendations/BulkRejectModal";
import { useRecommendationSelection } from "../hooks/useRecommendationSelection";
import {
  getAdminRecommendations,
  bulkApproveRecommendations,
  bulkRejectRecommendations,
  CallRecommendation,
  RecommendationsResponse,
  BulkOperationResult,
} from "../services/grantsApi";
import { describeBulkItemError } from "../components/grants/recommendations/recommendationReviewMessages";

/**
 * Action type badge colors and labels
 */
const ACTION_BADGES: Record<
  string,
  { label: string; className: string }
> = {
  create_new: {
    label: "Nowy nabór",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  update_existing: {
    label: "Aktualizacja",
    className: "bg-yellow-100 text-yellow-900 border-yellow-200",
  },
  link_to_existing: {
    label: "Powiązanie",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
};

/**
 * Recommendation status badge colors and labels
 */
const STATUS_BADGES: Record<
  string,
  { label: string; className: string }
> = {
  pending_review: {
    label: "Oczekuje",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  auto_approved: {
    label: "Auto",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  approved: {
    label: "Zatwierdzona",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  rejected: {
    label: "Odrzucona",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

/**
 * Status filter options
 */
const STATUS_OPTIONS = [
  { value: "all", label: "Wszystkie" },
  { value: "pending_review", label: "Oczekujące" },
  { value: "auto_approved", label: "Auto-zatwierdzone" },
  { value: "approved", label: "Zatwierdzone" },
  { value: "rejected", label: "Odrzucone" },
];

/**
 * Returns color class for confidence score
 */
const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return "text-green-600";
  if (confidence >= 0.6) return "text-yellow-600";
  return "text-red-600";
};

/**
 * Skeleton row for loading state
 */
const SkeletonRow = ({ showCheckbox, showStatusColumn }: { showCheckbox: boolean; showStatusColumn: boolean }) => (
  <tr className="animate-pulse">
    {showCheckbox && (
      <td className="px-4 py-4 w-12">
        <div className="h-4 w-4 bg-neutral-200 rounded"></div>
      </td>
    )}
    <td className="px-4 py-4">
      <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-5 bg-neutral-200 rounded w-24"></div>
    </td>
    {showStatusColumn && (
      <td className="px-4 py-4">
        <div className="h-5 bg-neutral-200 rounded w-20"></div>
      </td>
    )}
    <td className="px-4 py-4 text-right">
      <div className="h-4 bg-neutral-200 rounded w-12 ml-auto"></div>
    </td>
    <td className="px-4 py-4 text-right">
      <div className="h-4 bg-neutral-200 rounded w-20 ml-auto"></div>
    </td>
  </tr>
);

interface RecommendationRowProps {
  recommendation: CallRecommendation;
  onNavigate: (id: string) => void;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  showCheckbox: boolean;
  showStatusColumn: boolean;
  isSelectionDisabled: boolean;
}

/**
 * Table row for a single recommendation
 */
const RecommendationRow = React.memo(function RecommendationRow({
  recommendation,
  onNavigate,
  isSelected,
  onToggleSelection,
  showCheckbox,
  showStatusColumn,
  isSelectionDisabled,
}: RecommendationRowProps) {
  const actionBadge = ACTION_BADGES[recommendation.suggestedAction] || {
    label: recommendation.suggestedAction,
    className: "bg-neutral-100 text-neutral-800",
  };

  const statusBadge = STATUS_BADGES[recommendation.status] || {
    label: recommendation.status,
    className: "bg-neutral-100 text-neutral-800",
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelection(recommendation.id);
  };

  return (
    <tr
      className="hover:bg-neutral-50 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      onClick={() => onNavigate(recommendation.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate(recommendation.id);
        }
      }}
      tabIndex={0}
      role="row"
      data-testid={`recommendation-row-${recommendation.id}`}
    >
      {showCheckbox && (
        <td className="px-4 py-4 w-12">
          <input
            type="checkbox"
            checked={isSelected}
            onClick={handleCheckboxClick}
            disabled={isSelectionDisabled}
            aria-label={`Zaznacz rekomendację: ${recommendation.article?.title || "Brak tytułu"} (ID: ${recommendation.id.substring(0, 8)})`}
            data-testid="recommendation-checkbox"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          />
        </td>
      )}
      <td className="px-4 py-4">
        <div className="text-sm font-medium text-neutral-900 max-w-md truncate text-left">
          {recommendation.article?.title || "Brak tytułu artykułu"}
        </div>
        {recommendation.article?.url && (
          <a
            href={recommendation.article.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
          >
            Zobacz artykuł
          </a>
        )}
      </td>
      <td className="px-4 py-4">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${actionBadge.className}`}
        >
          {actionBadge.label}
        </span>
        {recommendation.targetCall && (
          <div className="text-xs text-neutral-500 mt-1 truncate max-w-[150px]">
            → {recommendation.targetCall.title}
          </div>
        )}
      </td>
      {showStatusColumn && (
        <td className="px-4 py-4">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadge.className}`}
          >
            {statusBadge.label}
          </span>
        </td>
      )}
      <td className="px-4 py-4 text-right">
        <span
          className={`text-sm font-semibold ${getConfidenceColor(recommendation.confidence)}`}
          title={`Dokładna wartość: ${(recommendation.confidence * 100).toFixed(1)}%`}
        >
          {(recommendation.confidence * 100).toFixed(0)}%
        </span>
      </td>
      <td className="px-4 py-4 text-right">
        <span className="text-sm text-neutral-500">
          {new Date(recommendation.createdAt).toLocaleDateString("pl-PL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </td>
    </tr>
  );
});

/**
 * Admin page for managing grant call recommendations.
 * Shows list of recommendations with filtering by status.
 * Allows admin to approve or reject recommendations.
 * Supports bulk approve/reject for pending_review status.
 */
type StatusType = "all" | "pending_review" | "approved" | "rejected" | "auto_approved";

/**
 * AC-4: number of bulk-approved rows whose action was create_new but that
 * were silently redirected to update_existing (callNumber collision).
 * `approved` is only present on bulk-approve results (undefined for reject).
 */
const countRedirectedToUpdate = (result: BulkOperationResult): number =>
  (result.approved ?? []).filter(
    (item) => item.action === "create_new" && !item.isNewCall
  ).length;

/**
 * Longest article title (including the ellipsis) quoted in a bulk-result toast
 */
const BULK_ERROR_TITLE_MAX_LENGTH = 60;

/**
 * Bulk results with per-item errors are long; the length-based default
 * duration (capped at 10 s) is too short to read them. The toast can still
 * be closed early.
 */
const BULK_ERROR_TOAST_DURATION_MS = 20000;

/**
 * Polish plural for the number of bulk errors not shown in the toast
 */
const formatOtherErrorsCount = (count: number): string => {
  if (count === 1) return "1 inny błąd";
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
    return `${count} inne błędy`;
  }
  return `${count} innych błędów`;
};

/**
 * Names the recommendation a bulk error concerns: its (truncated) article
 * title when known, otherwise a short id.
 */
const formatBulkErrorItemLabel = (id: string, title: string | undefined): string => {
  if (!title) return `rekomendacja #${id.substring(0, 8)}`;
  const shortTitle =
    title.length > BULK_ERROR_TITLE_MAX_LENGTH
      ? `${title.substring(0, BULK_ERROR_TITLE_MAX_LENGTH - 1)}…`
      : title;
  return `„${shortTitle}”`;
};

/**
 * Explicit toast duration for bulk results that list per-item errors
 * (undefined keeps the default length-based duration).
 */
const getBulkResultToastDuration = (result: BulkOperationResult): number | undefined =>
  result.errors?.length ? BULK_ERROR_TOAST_DURATION_MS : undefined;

/**
 * Format bulk operation result message.
 * Per-item errors (`result.errors[]`) are summarized as the first error's text
 * and the recommendation it concerns, plus a count of the others, so the
 * moderator learns why items failed without the toast growing unbounded.
 *
 * @param getItemTitle - Resolves a recommendation id to its article title
 */
export const formatBulkResultMessage = (
  result: BulkOperationResult,
  action: "zatwierdzon" | "odrzucon",
  getItemTitle?: (id: string) => string | undefined
): string => {
  const suffix = result.success === 1 ? "a" : result.success < 5 ? "e" : "ych";
  const base =
    result.failed === 0
      ? `${action === "zatwierdzon" ? "Zatwierdzono" : "Odrzucono"} ${result.success} rekomendacj${suffix === "a" ? "ę" : suffix === "e" ? "e" : "i"}`
      : `${action === "zatwierdzon" ? "Zatwierdzono" : "Odrzucono"}: ${result.success}, błędy: ${result.failed}`;

  const redirectedCount = countRedirectedToUpdate(result);
  const summary =
    redirectedCount > 0
      ? `${base} — ${redirectedCount} przekierowano na aktualizację (kolizja numeru naboru)`
      : base;

  const [firstError, ...otherErrors] = result.errors ?? [];
  if (!firstError) return summary;

  const itemLabel = formatBulkErrorItemLabel(firstError.id, getItemTitle?.(firstError.id));
  const details = `${summary}. ${itemLabel}: ${describeBulkItemError(firstError.error)}`;
  return otherErrors.length > 0
    ? `${details} (i ${formatOtherErrorsCount(otherErrors.length)})`
    : details;
};

/**
 * AC-4: bulk-approve toast type — a silent create_new -> update_existing
 * redirect warrants a warning even when every item nominally "succeeded",
 * same as the single-recommendation approve flow. Failures still take
 * priority (existing behavior), since those need attention first.
 */
export const getBulkResultToastType = (
  result: BulkOperationResult
): "success" | "warning" | "info" => {
  if (result.failed > 0) return "info";
  return countRedirectedToUpdate(result) > 0 ? "warning" : "success";
};

const GrantsAdminRecommendations: React.FC = () => {
  const { user, hasModuleEnabled } = useHotel();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Mirror ModeratorRoute: global admin OR kiedynabory moderator module.
  const canModerate =
    user?.role === "admin" || hasModuleEnabled("admin:kiedynabory:write");
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStatus = (searchParams.get("status") as StatusType) || "pending_review";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const articleId = searchParams.get("articleId") || undefined;

  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false);

  // Selection state for bulk operations
  const {
    selectedIds,
    toggleSelection,
    clearSelection,
    isSelected,
    selectedCount,
    toggleAll,
    isAllSelected,
  } = useRecommendationSelection();

  // Only show bulk actions for pending_review status
  const showBulkActions = currentStatus === "pending_review";

  // Show status column only when viewing all statuses
  const showStatusColumn = currentStatus === "all";

  const { data, isLoading, error, refetch } = useQuery<RecommendationsResponse>({
    queryKey: ["grants-admin-recommendations", currentStatus, currentPage, articleId],
    queryFn: () =>
      getAdminRecommendations({
        page: currentPage,
        limit: 20,
        status: currentStatus,
        articleId,
      }),
  });

  // Names the recommendation a bulk error concerns (rows off the current page
  // fall back to a short id in formatBulkResultMessage)
  const getRecommendationTitle = useCallback(
    (id: string) => data?.data?.find((rec) => rec.id === id)?.article?.title,
    [data?.data]
  );

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: (ids: string[]) => bulkApproveRecommendations(ids),
    onSuccess: (result) => {
      const message = formatBulkResultMessage(result, "zatwierdzon", getRecommendationTitle);
      addToast({
        message,
        type: getBulkResultToastType(result),
        duration: getBulkResultToastDuration(result),
      });
      clearSelection();
      queryClient.invalidateQueries({ queryKey: ["grants-admin-recommendations"] });
    },
    onError: (error: Error) => {
      addToast({ message: `Operacja nie powiodła się: ${error.message}`, type: "error" });
    },
  });

  // Bulk reject mutation
  const bulkRejectMutation = useMutation({
    mutationFn: ({ ids, notes }: { ids: string[]; notes: string }) =>
      bulkRejectRecommendations(ids, notes),
    onSuccess: (result) => {
      const message = formatBulkResultMessage(result, "odrzucon", getRecommendationTitle);
      addToast({
        message,
        type: getBulkResultToastType(result),
        duration: getBulkResultToastDuration(result),
      });
      clearSelection();
      setIsBulkRejectModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["grants-admin-recommendations"] });
    },
    onError: (error: Error) => {
      addToast({ message: `Operacja nie powiodła się: ${error.message}`, type: "error" });
    },
  });

  const isBulkOperationLoading = bulkApproveMutation.isPending || bulkRejectMutation.isPending;

  // Get IDs of recommendations on current page
  const currentPageIds = useMemo(
    () => data?.data?.map((rec) => rec.id) || [],
    [data?.data]
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      clearSelection();
      const params: Record<string, string> = { status, page: "1" };
      if (articleId) {
        params.articleId = articleId;
      }
      setSearchParams(params);
    },
    [setSearchParams, articleId, clearSelection]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      clearSelection();
      const params: Record<string, string> = { status: currentStatus, page: page.toString() };
      if (articleId) {
        params.articleId = articleId;
      }
      setSearchParams(params);
    },
    [setSearchParams, currentStatus, articleId, clearSelection]
  );

  const handleClearArticleFilter = useCallback(() => {
    clearSelection();
    setSearchParams({ status: currentStatus, page: "1" });
  }, [setSearchParams, currentStatus, clearSelection]);

  /**
   * Navigate to recommendation details page preserving query params.
   * Used as onClick handler for table rows - navigates to details view.
   * Checkboxes use stopPropagation to prevent this navigation when selecting items.
   * @see docs/specifications/2026-01-29-recommendation-modal-context-links.md
   */
  const handleRowNavigate = useCallback((id: string) => {
    const params = new URLSearchParams();
    params.set("page", currentPage.toString());
    params.set("status", currentStatus);
    navigate(`/grants/recommendations/${id}?${params.toString()}`);
  }, [navigate, currentPage, currentStatus]);

  // Bulk action handlers
  const handleBulkApprove = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length > 0) {
      bulkApproveMutation.mutate(ids);
    }
  }, [selectedIds, bulkApproveMutation]);

  const handleBulkRejectClick = useCallback(() => {
    setIsBulkRejectModalOpen(true);
  }, []);

  const handleBulkRejectSubmit = useCallback(
    (notes: string) => {
      const ids = Array.from(selectedIds);
      if (ids.length > 0) {
        bulkRejectMutation.mutate({ ids, notes });
      }
    },
    [selectedIds, bulkRejectMutation]
  );

  const handleBulkRejectModalClose = useCallback(() => {
    setIsBulkRejectModalOpen(false);
  }, []);

  const handleToggleAllOnPage = useCallback(() => {
    toggleAll(currentPageIds);
  }, [toggleAll, currentPageIds]);

  if (!canModerate) {
    return (
      <Layout>
        <div className="p-6 text-center text-red-600">
          Brak uprawnień do tej strony
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumbs */}
        <nav className="text-sm text-neutral-500">
          <Link to="/grants/calls" className="hover:text-primary-600">
            Nabory
          </Link>
          <span className="mx-2">&gt;</span>
          <span className="text-neutral-900">Rekomendacje</span>
        </nav>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 text-left">
              Rekomendacje naborów
            </h1>
            <p className="mt-1 text-sm text-neutral-600 text-left">
              Przejrzyj automatyczne sugestie i zatwierdź lub odrzuć
            </p>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 border-b border-neutral-200 pb-2">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              className={`px-4 py-2 rounded-t-md text-sm font-medium transition-colors ${
                currentStatus === option.value
                  ? "bg-primary-600 text-white"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Article filter chip */}
        {articleId && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600">Filtrowanie po artykule:</span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800 border border-primary-200">
              <span className="max-w-[200px] truncate">{articleId}</span>
              <button
                onClick={handleClearArticleFilter}
                className="ml-1 text-primary-600 hover:text-primary-800"
                title="Usuń filtr"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {showBulkActions && (
          <BulkActionsToolbar
            selectedCount={selectedCount}
            totalCount={data?.data?.length || 0}
            onApprove={handleBulkApprove}
            onReject={handleBulkRejectClick}
            isLoading={isBulkOperationLoading}
          />
        )}

        {/* Table */}
        <div className={`bg-white rounded-lg shadow-sm border border-neutral-200 ${isBulkOperationLoading ? 'opacity-70' : ''}`}>
          {error ? (
            <div className="p-6 text-center">
              <p className="text-red-600 mb-2">Błąd podczas pobierania danych</p>
              <button
                onClick={() => refetch()}
                className="text-primary-600 hover:text-primary-800 inline-flex items-center"
              >
                <SyncIcon className="h-4 w-4 mr-1" />
                Spróbuj ponownie
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    {showBulkActions && (
                      <th className="px-4 py-3 w-12">
                        <input
                          type="checkbox"
                          checked={currentPageIds.length > 0 && isAllSelected(currentPageIds)}
                          onChange={handleToggleAllOnPage}
                          disabled={isLoading || isBulkOperationLoading || currentPageIds.length === 0}
                          aria-label="Zaznacz wszystkie rekomendacje na stronie"
                          data-testid="select-all-checkbox"
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Artykuł
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                      Akcja
                    </th>
                    {showStatusColumn && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                        Status
                      </th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Pewność
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Data utworzenia
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {isLoading ? (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} showCheckbox={showBulkActions} showStatusColumn={showStatusColumn} />
                      ))}
                    </>
                  ) : (
                    data?.data?.map((rec) => (
                      <RecommendationRow
                        key={rec.id}
                        recommendation={rec}
                        onNavigate={handleRowNavigate}
                        isSelected={isSelected(rec.id)}
                        onToggleSelection={toggleSelection}
                        showCheckbox={showBulkActions}
                        showStatusColumn={showStatusColumn}
                        isSelectionDisabled={isBulkOperationLoading}
                      />
                    ))
                  )}
                </tbody>
              </table>

              {/* Empty state */}
              {!isLoading && (!data?.data || data.data.length === 0) && (
                <EmptyState
                  icon={<ClockIcon className="h-12 w-12 mx-auto" />}
                  title={
                    articleId
                      ? "Brak rekomendacji dla tego artykułu"
                      : currentStatus === "all"
                      ? "Brak rekomendacji"
                      : currentStatus === "pending_review"
                      ? "Brak rekomendacji do przejrzenia"
                      : currentStatus === "auto_approved"
                      ? "Brak auto-zatwierdzonych rekomendacji"
                      : currentStatus === "approved"
                      ? "Brak zatwierdzonych rekomendacji"
                      : "Brak odrzuconych rekomendacji"
                  }
                  description={
                    articleId
                      ? "Ten artykuł nie ma jeszcze żadnych rekomendacji w wybranym statusie."
                      : undefined
                  }
                  action={{
                    label: "Odśwież",
                    onClick: () => refetch(),
                    icon: <SyncIcon className="-ml-0.5 h-5 w-5" />,
                  }}
                  className="p-6"
                />
              )}

              {/* Pagination */}
              {data?.pagination && data.pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between">
                  <div className="text-sm text-neutral-500">
                    Strona {data.pagination.page} z {data.pagination.totalPages} (
                    {data.pagination.totalCount} rekomendacji)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!data.pagination.hasPrevPage}
                      className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Poprzednia
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!data.pagination.hasNextPage}
                      className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk reject modal */}
        <BulkRejectModal
          isOpen={isBulkRejectModalOpen}
          onClose={handleBulkRejectModalClose}
          onSubmit={handleBulkRejectSubmit}
          selectedCount={selectedCount}
          isLoading={bulkRejectMutation.isPending}
        />
      </div>
    </Layout>
  );
};

export default GrantsAdminRecommendations;
